import { createSlice } from '@reduxjs/toolkit';

import { initializeAuth, login, resendCode, verifyMfa } from './auth-api';
import { logout } from './logout';
import { fetchUserPermissions } from './permissions-api';
import type { User } from '@/types/auth';

export interface AuthState {
    user: User | null;
    isLoggedIn: boolean;
    isInitializing: boolean;
    pendingEmail: string | null;
    pendingPassword: string | null;
    session: string | null;
    permissions: string[];
}

const initialState: AuthState = {
    user: null,
    isLoggedIn: false,
    isInitializing: true,
    pendingEmail: null,
    pendingPassword: null,
    session: null,
    permissions: []
};

const resetAuthSession = (state: AuthState) => {
    state.user = null;
    state.isLoggedIn = false;
    state.isInitializing = false;
    state.pendingEmail = null;
    state.pendingPassword = null;
    state.session = null;
    state.permissions = [];
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearMfaState: (state) => {
            state.pendingEmail = null;
            state.session = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(logout, (state) => {
                resetAuthSession(state);
            })
            .addCase(initializeAuth.pending, (state) => {
                state.isInitializing = true;
            })
            .addCase(initializeAuth.fulfilled, (state, { payload }) => {
                state.user = payload.user;
                state.isLoggedIn = payload.isLoggedIn;
                state.isInitializing = false;
                state.pendingEmail = null;
                state.session = null;
                if (!payload.isLoggedIn) {
                    state.permissions = [];
                }
            })
            .addCase(initializeAuth.rejected, (state) => {
                resetAuthSession(state);
            })

            .addCase(login.fulfilled, (state, { payload }) => {
                state.user = null;
                state.isLoggedIn = false;
                state.isInitializing = false;
                state.pendingEmail = payload.email;
                state.pendingPassword = payload.password;
                state.session = payload.session;
                state.permissions = [];
            })
            .addCase(login.rejected, (state) => {
                resetAuthSession(state);
            })
            .addCase(resendCode.fulfilled, (state, { payload }) => {
                state.session = payload.session;
            })
            .addCase(verifyMfa.fulfilled, (state, { payload }) => {
                state.user = payload.user;
                state.isLoggedIn = true;
                state.isInitializing = false;
                state.pendingEmail = null;
                state.session = null;
            })
            .addCase(verifyMfa.rejected, (state) => {
                state.permissions = [];
            })
            .addCase(fetchUserPermissions.fulfilled, (state, { payload }) => {
                state.permissions = payload;
            })
            .addCase(fetchUserPermissions.rejected, (state) => {
                state.permissions = [];
            });
    }
});

export const { clearMfaState } = authSlice.actions;
export { logout };
export default authSlice.reducer;
